// Ref-counted topic sockets — the shared connection manager both realtime
// custom elements sit on top of.
//
// This replaces two near-identical copies of the same lifecycle logic that had
// drifted apart: `turbo_mercure_stream_source.js` ref-counted its sockets per
// topic, `mercure_data_source.js` opened one per element and never initialised
// its own give-up counters (they were `undefined`, and the guard worked by
// accident through `|| 0`). One implementation, one set of semantics, one place
// to fix.
//
// ── There is one socket per (hub, topic) pair, and that is not an optimisation
//
// Mercure multiplexed many topics onto one SSE connection via repeated
// `?topic=`. A topic is now a Durable Object, so topics cannot share a socket —
// but two ELEMENTS naming the same topic can, and on a page that mounts the
// same feed twice they do. The socket closes when the last element releases it.

import { type RealtimeFrame, parseFrame } from "./frame";

const RECONNECT_INITIAL_MS = 1_000;
const RECONNECT_FACTOR = 2;
const RECONNECT_MAX_MS = 30_000;

/**
 * Consecutive failed handshakes before a topic stops retrying.
 *
 * WHAT COUNTS AS A FAILED HANDSHAKE, AND WHY THE OLD RULE WAS WRONG. The server
 * refuses BEFORE a WebSocket exists — 403 from the authorizer, 302 from the
 * session gate, 503 when the binding is unbound — so there is no socket to
 * carry close code 1008 and the browser reports 1006. Stopping only on 1008
 * therefore never stops.
 *
 * The previous rule additionally required that the socket had NEVER opened
 * (`everOpened`), which latched `true` on the first success and never cleared.
 * The single most likely reason for a page to start being refused is the exact
 * case it excluded: a session that expired while the tab sat open. Every mount
 * point then retried forever at the 30s ceiling, each attempt costing the
 * server a session read and a user query — the failure mode the guard was
 * written to prevent, surviving the guard.
 *
 * Counting CONSECUTIVE failures and resetting on open covers both: a flaky
 * network still reconnects indefinitely because each success clears the count,
 * and a persistent refusal stops whether or not the tab was ever connected.
 */
const MAX_FAILED_HANDSHAKES = 6;

/** RFC 6455 §7.4.1 — an explicit refusal on an open socket. Unreachable today. */
const CLOSE_POLICY_VIOLATION = 1008;
/** Our own deliberate close, so `onclose` knows not to reconnect. */
const CLOSE_NORMAL = 1000;

/**
 * Keepalive period. The Durable Object registers
 * `setWebSocketAutoResponse("ping" → "pong")`, which the runtime answers
 * WITHOUT waking the hibernated object — so this costs no duration billing and
 * no CPU. Until now nothing sent the ping, and the auto-response answered a
 * message that was never sent: an idle socket behind a NAT or a proxy with an
 * idle timeout went half-open, and neither end noticed. The page kept a socket
 * it believed was live and silently received nothing.
 */
const KEEPALIVE_MS = 30_000;
/** Unanswered pings tolerated before we treat the socket as dead and reopen. */
const MAX_MISSED_PONGS = 2;
const PING = "ping";
const PONG = "pong";

/** What a subscriber is handed. Frames of the other kind never reach it. */
export type FrameHandler = (frame: RealtimeFrame) => void;

/** Opaque handle returned by `subscribe`; call `release` on disconnect. */
export interface Subscription {
  release(): void;
}

/**
 * Timer handles, typed as whatever the ambient lib actually returns.
 *
 * `lib.dom` types `setTimeout` as returning `number`; `@types/node` types it as
 * `NodeJS.Timeout`. Which one is in scope is decided by the tsconfig, not by
 * this file: `packages/client/tsconfig.json` sets `lib: ["es2022", "dom",
 * "dom.iterable"]` with `types: []`, so here it is `number` — verified, not
 * assumed (`const x: null = setTimeout(...)` reports `Type 'number'`).
 *
 * Both call sites used to force that through `unknown` into `number`, which §11
 * bans by name. The double cast is worse than redundant here: it is a cast that
 * can never fail, so it would go on compiling and start LYING the moment Node's
 * globals reached this program — a `types` entry, or a transitive
 * `/// <reference types="node" />` dragged in by a test dependency. The handles
 * would then really be `Timeout` objects wearing the type `number`, and the
 * identity check in `scheduleReconnect` would be comparing objects while the
 * compiler believed it was comparing numbers.
 *
 * `ReturnType<typeof setTimeout>` is true under either lib and needs no cast at
 * all. Deliberately NOT done: no `window.setTimeout` disambiguation (it forces
 * the DOM answer, which is the same lie in a nicer suit) and no widening to
 * `number | NodeJS.Timeout` (that names a type this package cannot see). The
 * handles are opaque — only ever compared by identity and handed back to
 * `clearTimeout`/`clearInterval` — so the return type of the function that
 * minted them is exactly the right type for them to carry.
 */
type TimeoutHandle = ReturnType<typeof setTimeout>;
type IntervalHandle = ReturnType<typeof setInterval>;

/**
 * The wire, behind the one shape the state machine actually uses.
 *
 * TWO WIRES, ONE MACHINE. A topic mounted with a PATH `hub-url` (`/realtime`)
 * is a Durable Object and opens a WebSocket; a topic mounted with an absolute
 * `http(s)` `hub-url` is the Mercure hub (ADR-0016) and opens an EventSource.
 * Backing off, giving up, reviving and ref-counting are identical either way,
 * and this file exists because the last time two realtime lifecycles were
 * written separately they drifted apart. So the wires differ in exactly three
 * places — how a connection is opened, whether it needs a keepalive, and what
 * a close looks like — and nothing else knows which one it is talking to.
 */
interface Transport {
  /**
   * Detach every handler and close, so no late event drives the machine.
   * Used when WE decide the connection is finished (deliberate release, or a
   * half-open socket the keepalive gave up on) and the caller sets the next
   * state itself.
   */
  abandon(reason: string): void;
  /**
   * Send one keepalive, or `null` where the wire keeps itself alive.
   *
   * `null` is not "skip the ping", it is "this wire has no such concept": SSE
   * is a plain HTTP response the hub writes a `:` heartbeat into every 30s
   * (the hub's `heartbeat 30s`, set explicitly for this reason), so there is
   * nothing for a client to send and nothing to count. The keepalive interval
   * is only started when this is non-null, which is what keeps `pulse` — and
   * its missed-pong reopen — off the SSE wire entirely.
   */
  readonly ping: (() => void) | null;
}

/**
 * A topic's connection state. Modelled as a union rather than a bag of
 * booleans so the impossible combinations — a pending retry timer on a
 * released topic, an open socket while "gave-up" — cannot be represented (§11).
 */
type ConnectionState =
  /** No socket and no timer. The state a fresh entry starts in. */
  | { readonly name: "idle" }
  /** Handshake in flight; the transport has not reported `open`. */
  | { readonly name: "connecting"; readonly transport: Transport }
  /**
   * Open and relaying. `keepalive` is the interval id, or `null` on a wire
   * that keeps itself alive (`Transport.ping === null`); `missedPongs` counts.
   */
  | {
      readonly name: "open";
      readonly transport: Transport;
      readonly keepalive: IntervalHandle | null;
    }
  /** Backing off; `timer` fires the next attempt. */
  | { readonly name: "waiting"; readonly timer: TimeoutHandle }
  /**
   * Stopped after MAX_FAILED_HANDSHAKES. NOT terminal — see `revive`. The
   * entry stays in the registry so its refs remain tracked, but it holds no
   * socket and no timer, so it costs nothing while it waits for a revival
   * signal.
   */
  | { readonly name: "gave-up" }
  /** Last element released the topic. Terminal; the entry is deleted. */
  | { readonly name: "released" };

interface Entry {
  readonly hubUrl: string;
  readonly topic: string;
  /**
   * The id of the last update this topic received, or null before the first.
   *
   * WHY IT SURVIVES A RECONNECT AND WHY THAT MATTERS. The hub ends a proxied
   * SSE response after about nine minutes (measured, 2026-09-09: heartbeats
   * every 30s to 514s, then a clean end of response at 541.7s). Reopening
   * from scratch would silently drop anything published in the gap, several
   * times an hour. Reopening from here does not.
   *
   * Only the Mercure wire sets it; the Durable Object wire has no equivalent
   * and leaves it null.
   */
  lastEventId: string | null;
  readonly handlers: Set<FrameHandler>;
  state: ConnectionState;
  /** Backoff exponent. Reset to 0 on every successful open. */
  attempt: number;
  /** Consecutive handshakes that failed. Reset to 0 on every successful open. */
  failedHandshakes: number;
  /** Pings sent since the last pong. Reset on every pong and on every open. */
  missedPongs: number;
}

const entries = new Map<string, Entry>();

const entryKey = (hubUrl: string, topic: string): string => `${hubUrl}\n${topic}`;

/**
 * Equal jitter — half the ceiling plus a random share of the other half. Full
 * jitter can retry almost immediately, which defeats backing off; a fixed delay
 * reconnects every tab in lockstep after an outage.
 */
function backoffDelay(attempt: number): number {
  const ceiling = Math.min(RECONNECT_MAX_MS, RECONNECT_INITIAL_MS * RECONNECT_FACTOR ** attempt);
  return Math.round(ceiling / 2 + Math.random() * (ceiling / 2));
}

/**
 * Which wire a `hub-url` names.
 *
 * The discriminator is the RAW attribute, not the resolved URL, and it has to
 * be: a path resolves against the page to `https://…` too, so resolving first
 * would send every Durable Object mount to the hub. `realtimeHubUrl(env)` on
 * the server writes one of exactly two things into the attribute — the tier's
 * `MERCURE_EDGE_HUB_URL` (absolute, `https://…/.well-known/mercure`) or
 * `REALTIME_MOUNT_PATH` (a path) — so the shapes cannot collide.
 */
function isHubUrl(hubUrl: string): boolean {
  return /^https?:\/\//i.test(hubUrl);
}

/**
 * The Mercure subscribe URL for one topic.
 *
 * ONE CONNECTION PER TOPIC, WHICH IS NOT WHAT RAILS DOES, AND THE REASON IS
 * ROUTING. `turbo_mercure_stream_source.js` keeps ONE EventSource per hub and
 * multiplexes every topic onto it with repeated `?topic=`. It can, because it
 * never has to know which topic an update came from: a Turbo Stream names its
 * own target DOM id, and a data frame is re-dispatched to the whole document.
 * We do have to know — several elements with DIFFERENT topics share a page,
 * and a frame delivered to the wrong one renders someone else's update (an
 * `append` action would do it repeatedly) — and a Mercure update does NOT
 * carry its topic to the subscriber. Multiplexing would therefore mean adding
 * the topic to the wire envelope the Durable Object also speaks, plus Rails'
 * rebuild-the-connection-on-every-mount, which a Turbo navigation does
 * constantly and our backoff would fight.
 *
 * What it costs: one HTTP connection per mounted topic, and the shell alone
 * mounts four. That is cheap over HTTP/2 (the hostname is proxied, and the
 * 6-per-host cap is an HTTP/1.1 rule) and it is the load-bearing assumption
 * here — if it turns out false, the fix is the envelope, not this function.
 */
export function hubStreamUrl(
  hubUrl: string,
  topic: string,
  lastEventId: string | null = null,
): string | null {
  let url: URL;
  try {
    url = new URL(hubUrl, window.location.href);
  } catch {
    console.warn("[realtime] unparseable hub-url", { hubUrl });
    return null;
  }
  url.searchParams.append("topic", topic);
  // Resume rather than restart. We close and reopen the stream ourselves, so
  // the browser never gets to send its own `Last-Event-ID` header; Mercure
  // accepts the same thing as a query parameter. PROBED against the staging
  // hub, not taken from the spec: publish while nobody is connected, reconnect
  // with the previous update's id, and the missed update arrives.
  if (lastEventId !== null && lastEventId !== "") {
    url.searchParams.append("lastEventID", lastEventId);
  }
  return url.toString();
}

/**
 * `hub-url` may be a path (`/realtime`) or absolute with an http(s) or ws(s)
 * scheme. Resolve against the page and upgrade the scheme.
 *
 * Only ever called for the WebSocket wire: an absolute `http(s)` hub-url is
 * the Mercure hub and goes to `hubStreamUrl` instead. The http(s) upgrade
 * below is still the main path, because that is what a PATH resolves to.
 */
export function socketUrl(hubUrl: string, topic: string): string | null {
  let url: URL;
  try {
    url = new URL(hubUrl, window.location.href);
  } catch {
    console.warn("[realtime] unparseable hub-url", { hubUrl });
    return null;
  }
  if (url.protocol === "http:") url.protocol = "ws:";
  else if (url.protocol === "https:") url.protocol = "wss:";
  url.searchParams.append("topic", topic);
  return url.toString();
}

/** Stop the keepalive interval attached to an open state, if it has one. */
/**
 * `turbo_mercure_stream_source.js`'s cookie recovery (Rails 5d74f72a).
 *
 * The hub cookie lasts an hour and an idle tab makes no request that would
 * re-mint it. When the hub ends that tab's stream, the reopen carries the
 * expired cookie and the hub REFUSES it (401) — and every retry after that is
 * refused the same way until the tab gives up. So after a refused hub
 * handshake, and only then, the retry first asks the app for a fresh cookie
 * (`POST /realtime/subscriber_cookie`, routes/realtime/index.ts).
 *
 * WHY "only after a refused handshake". Rails refreshes on `CLOSED`, which is
 * the browser's word for "the hub rejected the reconnect"; a stream the hub
 * merely ended reconnects by itself there. Here every error is ours to retry
 * (see `openHubStream`), and the hub ends a healthy stream about every nine
 * minutes, so refreshing on every retry would cost a request per tab per
 * nine minutes for nothing. A handshake that never opened is the refusal.
 *
 * 401/403 from the refresh means the session is gone: the topic stops, and the
 * tab becoming visible again revives it (`revive`), as in Rails. Any other
 * answer, or a network failure, reopens anyway — the reopen is the test.
 *
 * One request serves every topic retrying at once: the cookie is per browser,
 * not per stream.
 */
export const SUBSCRIBER_COOKIE_PATH = "/realtime/subscriber_cookie";

let cookieRefresh: Promise<number | null> | null = null;

function refreshSubscriberCookie(): Promise<number | null> {
  if (cookieRefresh !== null) return cookieRefresh;
  const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
  const pending = fetch(SUBSCRIBER_COOKIE_PATH, {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-CSRF-Token": token, Accept: "application/json" },
  })
    .then((res) => {
      console.log("[realtime] subscriber cookie refresh status=", res.status);
      return res.status;
    })
    .catch((error: unknown) => {
      console.warn("[realtime] subscriber cookie refresh failed", error);
      return null;
    })
    .finally(() => {
      if (cookieRefresh === pending) cookieRefresh = null;
    });
  cookieRefresh = pending;
  return pending;
}

function stopKeepalive(state: ConnectionState): void {
  if (state.name === "open" && state.keepalive !== null) clearInterval(state.keepalive);
}

function scheduleReconnect(entry: Entry): void {
  if (entry.state.name === "released" || entry.state.name === "waiting") return;
  const delay = backoffDelay(entry.attempt);
  entry.attempt += 1;
  // A refused hub handshake: ask for a fresh cookie while the backoff runs
  // (see `refreshSubscriberCookie`). Never on the Durable Object wire, whose
  // handshake authenticates with the session itself.
  const refresh =
    isHubUrl(entry.hubUrl) && entry.failedHandshakes > 0 ? refreshSubscriberCookie() : null;
  // Only proceed if this timer is still the current state — a release or a
  // revival between scheduling and firing wins. The refresh keeps the state
  // `waiting` until it answers, so the same check covers that wait too.
  const current = (): boolean => entry.state.name === "waiting" && entry.state.timer === timer;
  const reopen = (): void => {
    entry.state = { name: "idle" };
    openConnection(entry);
  };
  const timer = setTimeout(() => {
    if (!current()) return;
    if (refresh === null) {
      reopen();
      return;
    }
    void refresh.then((status) => {
      if (!current()) return;
      if (status === 401 || status === 403) {
        entry.state = { name: "gave-up" };
        console.warn(
          `[realtime] subscriber cookie refused; stopping topic=${entry.topic} until the tab is visible again`,
        );
        return;
      }
      reopen();
    });
  }, delay);
  entry.state = { name: "waiting", timer };
}

/** A frame arrived. Fan it out to every handler on this topic. */
function deliver(entry: Entry, data: unknown): void {
  if (data === PONG) {
    entry.missedPongs = 0;
    return;
  }
  const frame = parseFrame(data);
  if (frame === null) return;
  for (const handler of entry.handlers) {
    try {
      handler(frame);
    } catch (error) {
      // One element's render must not stop the others receiving the frame.
      console.error("[realtime] frame handler threw", error);
    }
  }
}

/**
 * Send a keepalive and notice when the far end has stopped answering. Two
 * unanswered pings (~60s) means the socket is half-open: it will never fire
 * `close` on its own, so we close it ourselves and let the normal backoff
 * reconnect.
 */
function pulse(entry: Entry): void {
  const state = entry.state;
  if (state.name !== "open") return;

  if (entry.missedPongs >= MAX_MISSED_PONGS) {
    console.warn(
      `[realtime] no pong in ${MAX_MISSED_PONGS} intervals; reopening topic=${entry.topic}`,
    );
    stopKeepalive(state);
    state.transport.abandon("keepalive timeout");
    entry.state = { name: "idle" };
    scheduleReconnect(entry);
    return;
  }

  entry.missedPongs += 1;
  // Unreachable on a wire whose `ping` is null — the interval that calls this
  // is only started when it is non-null — but the state cannot say so.
  state.transport.ping?.();
}

/**
 * The connection reported `open`. Shared by both wires: a stale report from a
 * transport we already replaced is ignored, the failure counters reset, and
 * the keepalive starts only on a wire that has one.
 */
function noteOpen(entry: Entry, transport: Transport): void {
  if (entry.state.name !== "connecting" || entry.state.transport !== transport) return;
  entry.attempt = 0;
  entry.failedHandshakes = 0;
  entry.missedPongs = 0;
  const keepalive = transport.ping === null ? null : setInterval(() => pulse(entry), KEEPALIVE_MS);
  entry.state = { name: "open", transport, keepalive };
  console.log("[realtime] open topic=", entry.topic);
}

/**
 * The connection is gone. Shared by both wires.
 *
 * `code` is the WebSocket close code, or `null` on a wire that has none — an
 * EventSource error carries no status at all, which is precisely why
 * MAX_FAILED_HANDSHAKES rather than a policy-violation code is what actually
 * stops a refused subscription.
 */
function noteClosed(entry: Entry, transport: Transport, code: number | null): void {
  const state = entry.state;
  // Terminal states own themselves: a late or duplicate close event must not
  // walk `gave-up` back to `idle`, even transiently.
  if (state.name === "released" || state.name === "gave-up") return;
  // A close belonging to a connection we already replaced is stale — ignore it.
  if ((state.name === "connecting" || state.name === "open") && state.transport !== transport) {
    return;
  }
  const wasOpen = state.name === "open";
  stopKeepalive(state);
  entry.state = { name: "idle" };

  if (!wasOpen) {
    // The handshake never completed: refused, unreachable, or the server is
    // mid-deploy. This is the ONLY signal a refusal produces.
    entry.failedHandshakes += 1;
    if (entry.failedHandshakes >= MAX_FAILED_HANDSHAKES) {
      entry.state = { name: "gave-up" };
      console.warn(
        `[realtime] giving up topic=${entry.topic} after ${entry.failedHandshakes} failed handshakes — refused, signed out, or unreachable`,
      );
      return;
    }
  }

  if (code === CLOSE_POLICY_VIOLATION) {
    entry.state = { name: "gave-up" };
    console.warn("[realtime] subscription refused topic=", entry.topic);
    return;
  }

  scheduleReconnect(entry);
}

/** The Durable Object wire: one WebSocket, ping/pong keepalive, close codes. */
function openSocket(entry: Entry): void {
  const url = socketUrl(entry.hubUrl, entry.topic);
  if (url === null) return;

  const socket = new WebSocket(url);
  const transport: Transport = {
    abandon(reason: string): void {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      try {
        socket.close(CLOSE_NORMAL, reason);
      } catch {
        // Closing an already-closed socket is not an error worth reporting.
      }
    },
    ping: (): void => {
      try {
        socket.send(PING);
      } catch {
        // A send on a dying socket throws; its `onclose` handles the rest.
      }
    },
  };
  entry.state = { name: "connecting", transport };

  socket.onopen = () => noteOpen(entry, transport);
  socket.onmessage = (event: MessageEvent) => deliver(entry, event.data);
  socket.onerror = () => {
    // The error event carries nothing useful and always precedes a close; the
    // close handler is where the decision is made. Logging the event object
    // here produced noise that read like a distinct failure and was not.
  };
  socket.onclose = (event: CloseEvent) => noteClosed(entry, transport, event.code);
}

/** The Mercure wire: one EventSource, no keepalive to send, no close codes. */
function openHubStream(entry: Entry): void {
  const url = hubStreamUrl(entry.hubUrl, entry.topic, entry.lastEventId);
  if (url === null) return;

  // `withCredentials` is what puts `mercureAuthorizationEdge` on the request:
  // the hub is a different host from the page, so the cookie only rides a CORS
  // request made with credentials — and the hub answers one only because its
  // `cors_origins` names this exact origin. `*` would not do, and not because
  // of the hub: a browser refuses to pair a wildcard origin with credentials.
  const source = new EventSource(url, { withCredentials: true });
  let finished = false;
  const transport: Transport = {
    abandon(): void {
      finished = true;
      source.onopen = null;
      source.onmessage = null;
      source.onerror = null;
      source.close();
    },
    ping: null,
  };
  entry.state = { name: "connecting", transport };

  source.onopen = () => noteOpen(entry, transport);
  source.onmessage = (event: MessageEvent) => {
    // Record BEFORE delivering: a handler that throws must not cost us the
    // resumption point, or the next reconnect replays what we already showed.
    if (event.lastEventId !== "") entry.lastEventId = event.lastEventId;
    deliver(entry, event.data);
  };
  source.onerror = () => {
    // WE OWN THE RETRY, NOT THE BROWSER. An EventSource has no close event and
    // no status: a refusal (401 from the hub, or a subscriber JWT whose
    // selectors do not cover this topic) and a dropped connection arrive as
    // the same bare error, and the browser then reconnects on its own schedule
    // — forever, invisibly to the give-up counter, which is exactly the
    // failure mode MAX_FAILED_HANDSHAKES exists to end. So we close it and run
    // the same backoff the socket wire runs.
    //
    // WHAT THAT COSTS, NAMED SO IT IS NOT REDISCOVERED AS A BUG: the browser's
    // own resumption replays missed updates via `Last-Event-ID`, and reopening
    // ourselves does not. Updates published while a tab was disconnected are
    // lost — which is already true of the Durable Object wire, so this is the
    // status quo rather than a regression, and it is the price of one retry
    // policy across both. Mercure accepts a `lastEventID` query parameter that
    // would restore it; adding it is a follow-up to be PROBED against the real
    // hub, not assumed from the spec.
    if (finished) return;
    transport.abandon("hub stream error");
    noteClosed(entry, transport, null);
  };
}

/** Open whichever wire this topic's `hub-url` names. */
function openConnection(entry: Entry): void {
  if (entry.state.name === "released" || entry.state.name === "gave-up") return;
  if (isHubUrl(entry.hubUrl)) openHubStream(entry);
  else openSocket(entry);
}

/**
 * Give a stopped topic another chance.
 *
 * WHY THIS EXISTS. Giving up used to be permanent AND invisible: the entry
 * stayed in the registry with `closed = true`, so every later element that
 * mounted the same topic found it, took the "already subscribed" branch, and
 * silently received nothing for the life of the page. A Turbo navigation
 * remounting the feed did not recover it; only a full reload did. Worse, the
 * threshold is reachable in ~16 seconds of backoff, which a deploy or a laptop
 * waking from sleep clears easily — so a transient outage could permanently
 * kill realtime on a tab that was otherwise fine.
 *
 * Revival is driven by the two signals that actually correlate with "the
 * reason we were failing may have changed": the browser reporting `online`
 * again, and the tab becoming visible. Both are free and neither polls.
 */
function revive(entry: Entry): void {
  if (entry.state.name !== "gave-up") return;
  if (entry.handlers.size === 0) return;
  entry.attempt = 0;
  entry.failedHandshakes = 0;
  entry.state = { name: "idle" };
  console.log("[realtime] retrying topic=", entry.topic);
  openConnection(entry);
}

function reviveAll(): void {
  for (const entry of entries.values()) revive(entry);
}

let revivalHooked = false;

function hookRevivalSignals(): void {
  if (revivalHooked) return;
  revivalHooked = true;
  window.addEventListener("online", reviveAll);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") reviveAll();
  });
}

/**
 * Subscribe `handler` to `topic` on `hubUrl`, opening the socket if this is the
 * first subscriber. Returns a handle whose `release` drops the reference and
 * closes the socket when it was the last one.
 *
 * Subscribing to a topic that had given up REVIVES it — mounting a fresh
 * element is itself evidence worth retrying on.
 */
export function subscribe(hubUrl: string, topic: string, handler: FrameHandler): Subscription {
  hookRevivalSignals();

  const key = entryKey(hubUrl, topic);
  let entry = entries.get(key);
  if (entry === undefined || entry.state.name === "released") {
    entry = {
      hubUrl,
      topic,
      handlers: new Set(),
      lastEventId: null,
      state: { name: "idle" },
      attempt: 0,
      failedHandshakes: 0,
      missedPongs: 0,
    };
    entries.set(key, entry);
  }
  const target = entry;
  target.handlers.add(handler);

  if (target.state.name === "gave-up") revive(target);
  else if (target.state.name === "idle") openConnection(target);

  return {
    release(): void {
      target.handlers.delete(handler);
      if (target.handlers.size > 0) return;

      const state = target.state;
      stopKeepalive(state);
      if (state.name === "waiting") clearTimeout(state.timer);
      if (state.name === "connecting" || state.name === "open") {
        state.transport.abandon("element disconnected");
      }
      target.state = { name: "released" };
      entries.delete(key);
      console.log("[realtime] closed topic=", target.topic);
    },
  };
}

/** Test seam: drop every entry so suites do not leak state into each other. */
export function resetForTests(): void {
  for (const entry of entries.values()) {
    const state = entry.state;
    stopKeepalive(state);
    if (state.name === "waiting") clearTimeout(state.timer);
    if (state.name === "connecting" || state.name === "open") {
      state.transport.abandon("test reset");
    }
    entry.state = { name: "released" };
  }
  entries.clear();
  cookieRefresh = null;
}

/** Test seam: the tuning constants, so a test asserts the real values. */
export const REALTIME_TUNING = {
  MAX_FAILED_HANDSHAKES,
  MAX_MISSED_PONGS,
  KEEPALIVE_MS,
  RECONNECT_MAX_MS,
} as const;
